import { useSelector } from 'react-redux';
import { RELATIONSHIP_TYPES } from '../constants/common/relationShipTypes';
import { ENROLLMENT_STATUSES } from '../constants/individualPortal/dashboard/dashboardModelTypes';
import { ALL_USER_ROLES } from '../constants/roles/userRoleTypes';
import { isNotEqualEffectiveDate, isWithinEffectiveRange } from '../modules/members/enrollments/common/enrollmentUtils';

const { BROKER, AGENCY_MANAGER, APPROVEDADMINSTAFFL1, APPROVEDADMINSTAFFL2, ASSISTER } = ALL_USER_ROLES;

type Permissions = string[];
type UserRole = string;

export const usePermissions = () => {
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};

    const isReinstatementPermission = (permissions: Permissions): boolean =>
        permissions.includes('MYAPP_REINST_ENROLLMENT');

    const isOverideEnrollmentPermission = (permissions: Permissions): boolean =>
        permissions.includes('MYAPP_ENRLMNT_OVRRD_ENRL_TO_CONFIRM');

    const isAgencyUser = (loggedInUserRole: UserRole): boolean =>
        [BROKER, AGENCY_MANAGER, APPROVEDADMINSTAFFL1, APPROVEDADMINSTAFFL2, ASSISTER].includes(loggedInUserRole);

    const isPremiumEditPermission = (userPermissions): boolean =>
        userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable;

    const isMemberEnrlmntAptcEditable = userPermissions =>
        userPermissions?.memberEnrlmntAptc || userPermissions?.memberEnrlmntAptcEditable;

    const isEnrlmntGroupMaxAptcEditable = (userPermissions): boolean =>
        userPermissions?.memberEnrlmntGroupMaxAptc || userPermissions?.memberEnrlmntGroupMaxAptcEditable;

    const isEnrollmentPendingOrPendCancelled = (enrollment): boolean =>
        enrollment?.isItf ||
        enrollment?.enrollmentStatus === ENROLLMENT_STATUSES.PEND ||
        enrollment?.enrollmentStatus === ENROLLMENT_STATUSES.PEND_CANCELLED;

    const isMemberEnrlmntEditSlcsps = (userPermissions): boolean =>
        userPermissions?.memberEnrlmntViewSlcsp || userPermissions?.memberEnrlmntEditSlcsp;

    const canResend834History = (userPermissions, enrollment) =>
        userPermissions?.memberEnrlmnt834hist &&
        ![ENROLLMENT_STATUSES.PEND, ENROLLMENT_STATUSES.PEND_CANCELLED].includes(enrollment?.enrollmentStatus);

    const canResendLatest834Transaction = (userPermissions, enrollment) =>
        userPermissions?.memberEnrlmntLtst834 &&
        enrollment.enrollmentStatus !== ENROLLMENT_STATUSES.PEND &&
        enrollment.enrollmentStatus !== ENROLLMENT_STATUSES.PEND_CANCELLED;

    const iseEnrolleeNameEditable = (userPermissions, enrollee) => {
        return (
            (!userPermissions?.memberEnrlmntMembrNmEditable && isSelfEnrollment(enrollee)) ||
            (!userPermissions?.memberEnrlmntMembrDpndntNmEditable && !isSelfEnrollment(enrollee))
        );
    };

    const isEnrolleeGenederEditable = (userPermissions, enrollee, enrollment) =>
        (!userPermissions?.memberEnrlmntMembrGndrEditable && isSelfEnrollment(enrollee)) ||
        (!userPermissions?.memberEnrlmntMembrDpndntGndrEditable && !isSelfEnrollment(enrollee)) ||
        enrollee.enrolleeEffectiveStartDateUI === enrollee.enrolleeEffectiveEndDateUI ||
        enrollment.enrollmentBenefitEffectiveStartDateUI === enrollment.enrollmentBenefitEffectiveEndDateUI;

    const isSelfEnrollment = enrollee =>
        enrollee.relationshipToSubscriber.toUpperCase() === RELATIONSHIP_TYPES.SELF.value;

    const viewEditableFields = (userPermissions, values, premium) =>
        isEnrlmntGroupMaxAptcEditable(userPermissions) &&
        isNotEqualEffectiveDate(values) &&
        isWithinEffectiveRange(premium.month, values);

    const viewSlcspField = (values, premium) =>
        isNotEqualEffectiveDate(values) && isWithinEffectiveRange(premium.month, values);

    const isEnrollmentActionsPermission = (permissions, enrollment) => {
        return (
            (permissions.includes('MYAPP_ENRLMNT_OVRRD_ENRL_TO_CONFIRM') &&
                [ENROLLMENT_STATUSES.PENDING, ENROLLMENT_STATUSES.PEND].includes(enrollment.enrollmentStatus) &&
                !enrollment.enrollmentConfirmationDate) ||
            (permissions.includes('MYAPP_REINST_ENROLLMENT') &&
                [ENROLLMENT_STATUSES.CANCEL, ENROLLMENT_STATUSES.TERMINATED].includes(enrollment.enrollmentStatus))
        );
    };

    const viewEnrollmentPremiumHistoryAction = enrollment => {
        return (
            userPermissions?.memberPrmHistActions &&
            (userPermissions?.memberPrmHistCancel || userPermissions?.memberPrmHistChange) &&
            enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND &&
            enrollment?.enrollmentStatus !== ENROLLMENT_STATUSES.PEND_CANCELED
        );
    };

    return {
        isOverideEnrollmentPermission,
        isEnrollmentActionsPermission,
        isReinstatementPermission,
        isAgencyUser,
        isPremiumEditPermission,
        isMemberEnrlmntAptcEditable,
        isEnrlmntGroupMaxAptcEditable,
        isEnrollmentPendingOrPendCancelled,
        isMemberEnrlmntEditSlcsps,
        canResend834History,
        canResendLatest834Transaction,
        iseEnrolleeNameEditable,
        isEnrolleeGenederEditable,
        isSelfEnrollment,
        viewEditableFields,
        viewEnrollmentPremiumHistoryAction,
        viewSlcspField,
    };
};
